import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./AlertsBudget.css";
import billing from "../../api/billing";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_BUDGET = 1000000;
const MAX_ALERT_EMAILS = 20;

const DEFAULT_SETTINGS = {
  voice: {
    enabled: false,
    overageBudget: "0.00",
    alertOnExceeded: false,
    pauseOnExceeded: false,
    emails: [],
  },
  sms: {
    enabled: false,
    overageBudget: "0.00",
    alertOnExceeded: false,
    pauseOnExceeded: false,
    emails: [],
  },
};

const cloneSettings = (settings) => ({
  voice: {
    ...settings.voice,
    emails: [...(settings.voice.emails || [])],
  },
  sms: {
    ...settings.sms,
    emails: [...(settings.sms.emails || [])],
  },
});

const formatBudgetInput = (value) => {
  const cleaned = String(value || "").replace(/[^\d.]/g, "");
  const parts = cleaned.split(".");
  if (parts.length <= 1) return cleaned;
  return `${parts[0]}.${parts.slice(1).join("").slice(0, 2)}`;
};

const formatBudgetFromApi = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "0.00";
  return num.toFixed(2);
};

const toBoolean = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

const normalizeChannelSettings = (channel = {}) => ({
  enabled: toBoolean(channel.enabled),
  overageBudget: formatBudgetFromApi(channel.overage_monthly_budget ?? 0),
  alertOnExceeded: toBoolean(channel.alert_on_exceeded),
  pauseOnExceeded: toBoolean(channel.pause_campaigns_on_exceeded),
  emails: Array.isArray(channel.alert_emails) ? channel.alert_emails : [],
});

const settingsFromApi = (data = {}) => ({
  voice: normalizeChannelSettings(data.voice),
  sms: normalizeChannelSettings(data.sms),
});

const settingsToApi = (settings) => ({
  voice: {
    enabled: settings.voice.enabled,
    overage_monthly_budget: Number(settings.voice.overageBudget || 0),
    alert_on_exceeded: settings.voice.alertOnExceeded,
    pause_campaigns_on_exceeded: settings.voice.pauseOnExceeded,
    alert_emails: settings.voice.emails,
  },
  sms: {
    enabled: settings.sms.enabled,
    overage_monthly_budget: Number(settings.sms.overageBudget || 0),
    alert_on_exceeded: settings.sms.alertOnExceeded,
    pause_campaigns_on_exceeded: settings.sms.pauseOnExceeded,
    alert_emails: settings.sms.emails,
  },
});

const ChannelSection = ({
  channelKey,
  title,
  iconClass,
  budgetHelper,
  alertHelper,
  pauseHelper,
  settings,
  draftEmail,
  budgetError,
  emailError,
  controlsDisabled,
  onToggle,
  onBudgetChange,
  onDraftEmailChange,
  onAddEmail,
  onRemoveEmail,
}) => {
  const channelOff = !settings.enabled;
  const fieldsDisabled = controlsDisabled || channelOff;

  return (
    <section
      className={`alerts-budget-card${
        channelOff || controlsDisabled ? " alerts-budget-card--disabled" : ""
      }`}
    >
      <div className="alerts-budget-card__header">
        <div className="alerts-budget-card__title-wrap">
          <span className="alerts-budget-card__icon" aria-hidden="true">
            <i className={iconClass} />
          </span>
          <h3 className="alerts-budget-card__title">{title}</h3>
        </div>
        <label className="alerts-budget-switch mb-0" title={settings.enabled ? "Enabled" : "Disabled"}>
          <input
            type="checkbox"
            checked={settings.enabled}
            onChange={(e) => onToggle(channelKey, "enabled", e.target.checked)}
            disabled={controlsDisabled}
            aria-label={`Enable ${title} alerts and budget`}
          />
          <span className="alerts-budget-slider" />
        </label>
      </div>

      <div className="alerts-budget-card__grid">
        <div className="alerts-budget-field">
          <label className="alerts-budget-field__label" htmlFor={`${channelKey}-budget`}>
            Overage Monthly Budget (USD)
          </label>
          <input
            id={`${channelKey}-budget`}
            type="text"
            inputMode="decimal"
            className="form-control alerts-budget-field__input"
            value={settings.overageBudget}
            onChange={(e) => onBudgetChange(channelKey, e.target.value)}
            disabled={fieldsDisabled}
            placeholder="0.00"
          />
          {budgetError ? <p className="alerts-budget-field__error">{budgetError}</p> : null}
          <p className="alerts-budget-field__help">{budgetHelper}</p>
        </div>

        <div className="alerts-budget-field">
          <span className="alerts-budget-field__label">Alert on Budget Exceeded</span>
          <label className="alerts-budget-switch alerts-budget-switch--inline mb-0">
            <input
              type="checkbox"
              checked={settings.alertOnExceeded}
              onChange={(e) => onToggle(channelKey, "alertOnExceeded", e.target.checked)}
              disabled={fieldsDisabled}
              aria-label={`Alert on budget exceeded for ${title}`}
            />
            <span className="alerts-budget-slider" />
          </label>
          <p className="alerts-budget-field__help">{alertHelper}</p>
        </div>

        <div className="alerts-budget-field">
          <span className="alerts-budget-field__label">Pause Campaigns on Budget Exceeded</span>
          <label className="alerts-budget-switch alerts-budget-switch--inline mb-0">
            <input
              type="checkbox"
              checked={settings.pauseOnExceeded}
              onChange={(e) => onToggle(channelKey, "pauseOnExceeded", e.target.checked)}
              disabled={fieldsDisabled}
              aria-label={`Pause campaigns on budget exceeded for ${title}`}
            />
            <span className="alerts-budget-slider" />
          </label>
          <p className="alerts-budget-field__help">{pauseHelper}</p>
        </div>

        <div className="alerts-budget-field">
          <label className="alerts-budget-field__label" htmlFor={`${channelKey}-email`}>
            Alert Emails
          </label>
          <div className={`alerts-budget-emails${fieldsDisabled ? " is-disabled" : ""}`}>
            <div className="alerts-budget-emails__tags">
              {settings.emails.map((email) => (
                <span key={email} className="alerts-budget-email-tag">
                  <span className="alerts-budget-email-tag__text">{email}</span>
                  <button
                    type="button"
                    className="alerts-budget-email-tag__remove"
                    onClick={() => onRemoveEmail(channelKey, email)}
                    disabled={fieldsDisabled}
                    aria-label={`Remove ${email}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <input
              id={`${channelKey}-email`}
              type="email"
              className="alerts-budget-emails__input"
              value={draftEmail}
              onChange={(e) => onDraftEmailChange(channelKey, e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onAddEmail(channelKey);
                }
              }}
              disabled={fieldsDisabled}
              placeholder="Add email and press Enter"
            />
          </div>
          {emailError ? <p className="alerts-budget-field__error">{emailError}</p> : null}
        </div>
      </div>
    </section>
  );
};

const AlertsBudget = ({ user }) => {
  const [savedSettings, setSavedSettings] = useState(() => cloneSettings(DEFAULT_SETTINGS));
  const [settings, setSettings] = useState(() => cloneSettings(DEFAULT_SETTINGS));
  const [draftEmails, setDraftEmails] = useState({ voice: "", sms: "" });
  const [budgetErrors, setBudgetErrors] = useState({ voice: "", sms: "" });
  const [emailErrors, setEmailErrors] = useState({ voice: "", sms: "" });
  const [saveMessage, setSaveMessage] = useState(null);
  const [billingCycleNote, setBillingCycleNote] = useState(
    "Budgets reset at the start of each billing cycle."
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loadError, setLoadError] = useState("");

  const isDirty = useMemo(
    () => JSON.stringify(settings) !== JSON.stringify(savedSettings),
    [settings, savedSettings]
  );

  const controlsDisabled = !hasLoaded || isLoading || isSaving;

  const themeStyle = useMemo(() => {
    const theme = user?.customizedDashboard?.[0];
    const headerColor = theme?.header_color?.trim() || "";
    const secondaryHeaderColor = theme?.secondary_header_color?.trim() || "";
    const startColor = headerColor || "#087ab6";
    const hasCustomPair = Boolean(headerColor && secondaryHeaderColor);

    return {
      "--alerts-budget-primary": hasCustomPair ? headerColor : startColor,
      "--alerts-budget-gradient": `linear-gradient(45deg, ${hasCustomPair ? headerColor : "#087ab6"}, ${hasCustomPair ? secondaryHeaderColor : "#00d39e"})`,
    };
  }, [user]);

  const applyApiSettings = useCallback((response) => {
    const payload = response?.data;
    if (!response?.status || !payload) {
      throw new Error(response?.message || "Unable to load alerts and budget settings.");
    }

    const nextSettings = settingsFromApi(payload);
    setSettings(nextSettings);
    setSavedSettings(cloneSettings(nextSettings));
    setBillingCycleNote(
      payload.billing_cycle_note || "Budgets reset at the start of each billing cycle."
    );
    setDraftEmails({ voice: "", sms: "" });
    setBudgetErrors({ voice: "", sms: "" });
    setEmailErrors({ voice: "", sms: "" });
    setHasLoaded(true);
    setLoadError("");
    return nextSettings;
  }, []);

  const loadSettings = useCallback(async () => {
    setIsLoading(true);
    setLoadError("");
    try {
      const response = await billing.getAlertsBudget();
      applyApiSettings(response);
      setSaveMessage(null);
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Unable to load alerts and budget settings.";
      setLoadError(message);
      setSaveMessage({
        type: "error",
        text: message,
      });
      // Do not apply defaults or clear previously loaded settings on failure.
    } finally {
      setIsLoading(false);
    }
  }, [applyApiSettings]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    if (!saveMessage) return undefined;
    const timer = setTimeout(() => setSaveMessage(null), 4000);
    return () => clearTimeout(timer);
  }, [saveMessage]);

  const updateChannel = (channelKey, patch) => {
    if (controlsDisabled) return;
    setSettings((prev) => ({
      ...prev,
      [channelKey]: {
        ...prev[channelKey],
        ...patch,
      },
    }));
  };

  const handleToggle = (channelKey, field, value) => {
    updateChannel(channelKey, { [field]: value });
  };

  const handleBudgetChange = (channelKey, value) => {
    updateChannel(channelKey, { overageBudget: formatBudgetInput(value) });
    if (budgetErrors[channelKey]) {
      setBudgetErrors((prev) => ({ ...prev, [channelKey]: "" }));
    }
  };

  const handleDraftEmailChange = (channelKey, value) => {
    if (controlsDisabled) return;
    setDraftEmails((prev) => ({ ...prev, [channelKey]: value }));
    if (emailErrors[channelKey]) {
      setEmailErrors((prev) => ({ ...prev, [channelKey]: "" }));
    }
  };

  const handleAddEmail = (channelKey) => {
    if (controlsDisabled) return;
    const email = String(draftEmails[channelKey] || "").trim().toLowerCase();
    if (!email) return;

    if (!EMAIL_REGEX.test(email)) {
      setEmailErrors((prev) => ({
        ...prev,
        [channelKey]: "Enter a valid email address.",
      }));
      return;
    }

    const existing = settings[channelKey].emails || [];
    if (existing.length >= MAX_ALERT_EMAILS) {
      setEmailErrors((prev) => ({
        ...prev,
        [channelKey]: `You can add up to ${MAX_ALERT_EMAILS} email addresses.`,
      }));
      return;
    }
    if (existing.includes(email)) {
      setEmailErrors((prev) => ({
        ...prev,
        [channelKey]: "This email is already added.",
      }));
      return;
    }

    updateChannel(channelKey, { emails: [...existing, email] });
    setDraftEmails((prev) => ({ ...prev, [channelKey]: "" }));
    setEmailErrors((prev) => ({ ...prev, [channelKey]: "" }));
  };

  const handleRemoveEmail = (channelKey, email) => {
    if (controlsDisabled) return;
    updateChannel(channelKey, {
      emails: (settings[channelKey].emails || []).filter((item) => item !== email),
    });
  };

  const handleCancel = () => {
    if (controlsDisabled || !hasLoaded) return;
    setSettings(cloneSettings(savedSettings));
    setDraftEmails({ voice: "", sms: "" });
    setBudgetErrors({ voice: "", sms: "" });
    setEmailErrors({ voice: "", sms: "" });
    setSaveMessage(null);
  };

  const validateSettings = () => {
    const nextBudgetErrors = { voice: "", sms: "" };
    const nextEmailErrors = { voice: "", sms: "" };
    let hasError = false;

    ["voice", "sms"].forEach((channelKey) => {
      const channel = settings[channelKey];
      const budget = Number(channel.overageBudget);
      const budgetEmpty = String(channel.overageBudget || "").trim() === "";

      if (budgetEmpty || !Number.isFinite(budget) || budget < 0 || budget > MAX_BUDGET) {
        nextBudgetErrors[channelKey] = `Budget must be between 0 and ${MAX_BUDGET.toLocaleString()}.`;
        hasError = true;
      } else if (channel.enabled && budget <= 0) {
        // Backend only enforces budgets greater than zero.
        nextBudgetErrors[channelKey] =
          "Enter a budget greater than $0.00 when this channel is enabled. A $0 budget does not enforce alerts or pause.";
        hasError = true;
      }

      if (channel.emails.length > MAX_ALERT_EMAILS) {
        nextEmailErrors[channelKey] = `You can add up to ${MAX_ALERT_EMAILS} email addresses.`;
        hasError = true;
      } else if (channel.emails.some((email) => !EMAIL_REGEX.test(email))) {
        nextEmailErrors[channelKey] = "All alert emails must be valid email addresses.";
        hasError = true;
      } else if (channel.enabled && channel.alertOnExceeded && channel.emails.length === 0) {
        nextEmailErrors[channelKey] = "Add at least one alert email when alerts are enabled.";
        hasError = true;
      }
    });

    if (hasError) {
      setBudgetErrors(nextBudgetErrors);
      setEmailErrors(nextEmailErrors);
      setSaveMessage({
        type: "error",
        text: "Please correct the alerts and budget settings before saving.",
      });
      return false;
    }

    setBudgetErrors({ voice: "", sms: "" });
    setEmailErrors({ voice: "", sms: "" });
    return true;
  };

  const handleSave = async () => {
    if (!hasLoaded || !validateSettings() || isSaving) return;

    setIsSaving(true);
    try {
      const response = await billing.saveAlertsBudget(settingsToApi(settings));
      applyApiSettings(response);
      setSaveMessage({
        type: "success",
        text: response.message || "Alerts & budget settings saved.",
      });
    } catch (error) {
      setSaveMessage({
        type: "error",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "Unable to save alerts and budget settings.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const showForm = hasLoaded && !isLoading;
  const showLoadFailure = !isLoading && !hasLoaded;

  return (
    <div className="alerts-budget-tab" style={themeStyle}>
      <p className="alerts-budget-tab__intro">
        Set overage budgets and alerts to monitor your usage and control campaign activity when
        limits are exceeded.
      </p>

      {isLoading ? (
        <div className="alerts-budget-loading" role="status">
          <i className="fa fa-spinner fa-spin" aria-hidden="true" /> Loading alerts and budget
          settings...
        </div>
      ) : null}

      {showLoadFailure ? (
        <div className="alerts-budget-load-error" role="alert">
          <p className="alerts-budget-load-error__text">
            {loadError || "Unable to load alerts and budget settings."}
          </p>
          <p className="alerts-budget-load-error__hint">
            Settings were not loaded. The form stays locked so existing Voice and SMS configuration
            cannot be overwritten by defaults. Retry until loading succeeds.
          </p>
          <button
            type="button"
            className="btn linear-gradient yr-submitbtn btn-sm alerts-budget-btn-save"
            onClick={loadSettings}
          >
            Retry
          </button>
        </div>
      ) : null}

      {showForm ? (
        <>
          <ChannelSection
            channelKey="voice"
            title="Voice (Outbound)"
            iconClass="fa fa-phone"
            budgetHelper="Set your monthly overage budget for outbound voice usage. Must be greater than $0.00 when enabled (backend does not enforce $0)."
            alertHelper="Receive email alerts when your voice spend exceeds the budget."
            pauseHelper="Automatically pause outbound voice campaigns when budget is exceeded."
            settings={settings.voice}
            draftEmail={draftEmails.voice}
            budgetError={budgetErrors.voice}
            emailError={emailErrors.voice}
            controlsDisabled={controlsDisabled}
            onToggle={handleToggle}
            onBudgetChange={handleBudgetChange}
            onDraftEmailChange={handleDraftEmailChange}
            onAddEmail={handleAddEmail}
            onRemoveEmail={handleRemoveEmail}
          />

          <ChannelSection
            channelKey="sms"
            title="SMS (Outbound)"
            iconClass="fa fa-comment"
            budgetHelper="Set your monthly overage budget for outbound SMS usage. Must be greater than $0.00 when enabled (backend does not enforce $0)."
            alertHelper="Receive email alerts when your SMS spend exceeds the budget."
            pauseHelper="Automatically pause outbound SMS campaigns when budget is exceeded."
            settings={settings.sms}
            draftEmail={draftEmails.sms}
            budgetError={budgetErrors.sms}
            emailError={emailErrors.sms}
            controlsDisabled={controlsDisabled}
            onToggle={handleToggle}
            onBudgetChange={handleBudgetChange}
            onDraftEmailChange={handleDraftEmailChange}
            onAddEmail={handleAddEmail}
            onRemoveEmail={handleRemoveEmail}
          />
        </>
      ) : null}

      {showForm ? (
        <div className="alerts-budget-footer">
          <div className="alerts-budget-note" role="note">
            <i className="fa fa-info-circle" aria-hidden="true" />
            <span>{billingCycleNote}</span>
          </div>
          <div className="alerts-budget-actions">
            <button
              type="button"
              className="btn linear-gradient-cancel yr-cancelbtn mg-r btn-sm"
              onClick={handleCancel}
              disabled={controlsDisabled || !isDirty}
            >
              <span>Cancel</span>
            </button>
            <button
              type="button"
              className="btn linear-gradient yr-submitbtn btn-sm alerts-budget-btn-save"
              onClick={handleSave}
              disabled={controlsDisabled || !isDirty}
            >
              {isSaving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      ) : null}

      {saveMessage && !showLoadFailure ? (
        <div
          className={`alerts-budget-toast alerts-budget-toast--${saveMessage.type}`}
          role="status"
        >
          {saveMessage.text}
        </div>
      ) : null}
    </div>
  );
};

export default AlertsBudget;
